import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ToggleTabsMenuComponent } from '../../../../shared/components/toggle-tabs-menu/toggle-tabs-menu.component';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { WorkerService } from '../../../worker/core/services/WorkerService';
import { IVoucherDetails } from '../../core/interfaces/IVoucherDetails';
import { IVoucherTransaction } from '../../core/interfaces/IVoucherTransaction';

@Component({
  selector: 'app-voucher-details-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    NumberToCurrencyPipe,
    CommonModule,
    LocaleDatetimeFormatPipe,
    ToggleTabsMenuComponent,
  ],
  templateUrl: './voucher-details-modal.component.html',
  styleUrl: './voucher-details-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VoucherDetailsModalComponent extends BaseModalComponent<IVoucherDetails> {

  public readonly voucherDetails = signal<IVoucherDetails | null>(null);
  public readonly voucherTransactions = signal<IVoucherTransaction[]>([]);
  public readonly selectedTabIndex = signal<number>(0);
  public readonly tabOptions = ['Details', 'Transactions'];
  public readonly isPrinting = signal(false);

  private readonly _workerService = inject(WorkerService);

  public override open(val?: IVoucherDetails, transactions?: IVoucherTransaction[]) {
    super.open(val);
    
    const voucherNumber = val?.voucherNumber;
    
    if (!voucherNumber) {
      return;
    }

    this.selectedTabIndex.set(0);
    
    if (val) {
      const initialDetails: IVoucherDetails = {
        voucherId: val.voucherId,
        voucherNumber: voucherNumber,
        balance: val.balance || 0,
        status: val.status || '',
        validUntil: val.validUntil || null,
        name: val.name || '',
        cancelInfo: val.cancelInfo || ''
      };
      this.voucherDetails.set(initialDetails);
      this.voucherTransactions.set(transactions || []);
    }
  }

  public onTabChange(index: number): void {
    this.selectedTabIndex.set(index);
    this.cdr.detectChanges();
  }

  public reprintVoucher(): void {
    const details = this.voucherDetails();
    if (!details?.voucherId || this.isPrinting()) {
      return;
    }

    this.isPrinting.set(true);
    this._workerService.printVoucher(details.voucherId)
      .pipe(finalize(() => this.isPrinting.set(false)))
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`print-voucher/notifications/${error.message}.Message`)
          );
        }
      });
  }
}
